import {
  Component,
  ChangeDetectionStrategy,
  DestroyRef,
  Input,
  computed,
  inject,
  signal,
} from "@angular/core";
import { trigger, transition, style, animate } from "@angular/animations";
import {
  GroupID,
  ShiftID,
  getCurrentShiftFromLocalTime,
} from "@app/models/dashboard.models";
import { DashboardStateService } from "@app/services/dashboard-state.service";

const SHIFT_SCHEDULE: Record<
  ShiftID,
  { start: string; end: string; next: ShiftID }
> = {
  A: { start: "7:00 AM", end: "7:00 PM", next: "B" },
  B: { start: "7:00 PM", end: "7:00 AM", next: "A" },
};

@Component({
  selector: "app-shift-announcement",
  templateUrl: "./shift-announcement.component.html",
  styleUrl: "./shift-announcement.component.scss",
  animations: [
    trigger("scaleIn", [
      transition(":enter", [
        style({ transform: "scale(0.8)", opacity: 0 }),
        animate("800ms ease-out", style({ transform: "scale(1)", opacity: 1 })),
      ]),
    ]),
    trigger("slideUp", [
      transition(":enter", [
        style({ transform: "translateY(50px)", opacity: 0 }),
        animate(
          "1000ms 300ms ease-out",
          style({ transform: "translateY(0)", opacity: 1 }),
        ),
      ]),
    ]),
    trigger("growLine", [
      transition(":enter", [
        style({ width: "0%" }),
        animate("1500ms 800ms ease-in-out", style({ width: "100%" })),
      ]),
    ]),
    trigger("fadeIn", [
      transition(":enter", [
        style({ opacity: 0 }),
        animate("500ms 1500ms ease-out", style({ opacity: 1 })),
      ]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ShiftAnnouncementComponent {
  private readonly stateService = inject(DashboardStateService);

  readonly timeRemaining = signal<string>("0h 0m 0s");
  private readonly nowTick = signal<Date>(new Date());

  @Input() rotationProgress?: number;

  readonly currentGroup = computed<GroupID>(() => this.stateService.currentGroup());
  readonly currentTurno = computed<ShiftID>(() =>
    getCurrentShiftFromLocalTime(this.nowTick()),
  );
  readonly nextTurno = computed(() => SHIFT_SCHEDULE[this.currentTurno()].next);
  readonly incomingGroup: GroupID = "C";
  readonly shiftSchedule = computed(() => SHIFT_SCHEDULE[this.currentTurno()]);
  readonly progressPercent = computed(() => {
    const secondsRemaining = this.extractSeconds(this.timeRemaining());
    const schedule = SHIFT_SCHEDULE[this.currentTurno()];
    const shiftStart = this.parseShiftTime(schedule.start, new Date());
    const shiftEnd = this.parseShiftTime(schedule.end, new Date());
    const totalShiftSeconds =
      (shiftEnd.getTime() - shiftStart.getTime()) / 1000;
    const elapsedSeconds = totalShiftSeconds - secondsRemaining;
    return Math.max(
      0,
      Math.min(100, (elapsedSeconds / totalShiftSeconds) * 100),
    );
  });

  constructor() {
    const tick = () => {
      this.nowTick.set(new Date());
      this.timeRemaining.set(this.calculateTimeRemaining(this.currentTurno()));
    };
    tick();
    const interval = setInterval(tick, 1000);
    inject(DestroyRef).onDestroy(() => clearInterval(interval));
  }

  private calculateTimeRemaining(shift: ShiftID): string {
    const now = new Date();
    const schedule = SHIFT_SCHEDULE[shift];
    const endTime = this.parseShiftTime(schedule.end, now);
    const remaining = Math.max(0, endTime.getTime() - now.getTime());
    const hours = Math.floor(remaining / 3600000);
    const minutes = Math.floor((remaining % 3600000) / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);
    return `${hours}h ${minutes}m ${seconds}s`;
  }

  private parseShiftTime(timeStr: string, referenceDate: Date): Date {
    const [time, period] = timeStr.split(" ");
    const [hours, minutes] = time.split(":").map(Number);
    const date = new Date(referenceDate);
    let hour = hours;
    if (period === "PM" && hours !== 12) hour += 12;
    if (period === "AM" && hours === 12) hour = 0;
    date.setHours(hour, minutes, 0, 0);
    if (date < referenceDate) {
      date.setDate(date.getDate() + 1);
    }
    return date;
  }

  private extractSeconds(timeStr: string): number {
    const match = timeStr.match(/(\d+)h (\d+)m (\d+)s/);
    if (!match) return 0;
    const [, h, m, s] = match;
    return parseInt(h) * 3600 + parseInt(m) * 60 + parseInt(s);
  }
}
